import React, { useState } from 'react';
import { Table, Card, Grid, Space, Typography, Pagination, Spin, Empty, Divider } from 'antd';

const { useBreakpoint } = Grid;
const { Text } = Typography;

/**
 * Tabla responsiva: en escritorio (>= md) es la <Table> de Ant Design de siempre;
 * en móvil (< md) renderiza cada fila como una tarjeta (etiqueta:valor apiladas) con
 * sus acciones al pie. Las tarjetas se derivan de las mismas `columns`, así que
 * convertir una tabla existente es cambiar <Table> por <ResponsiveTable>.
 *
 * Config opcional `mobile`:
 *   - title(record)      → título de la tarjeta (default: render de la 1ª columna)
 *   - subtitle(record)   → subtítulo bajo el título
 *   - actionsKey         → key de la columna de acciones (default 'actions')
 *   - hiddenKeys[]       → keys de columnas a omitir en el cuerpo
 */
const ResponsiveTable = ({ mobile = {}, ...props }) => {
  const screens = useBreakpoint();

  const {
    columns = [],
    dataSource = [],
    rowKey,
    loading = false,
    pagination,
    locale,
  } = props;

  const rows = Array.isArray(dataSource) ? dataSource : [];

  // Todos los hooks ANTES de cualquier return (rules-of-hooks): el estado de página
  // local solo se usa en móvil client-side, pero debe declararse siempre.
  const pag = pagination && typeof pagination === 'object' ? pagination : null;
  const [localPage, setLocalPage] = useState(pag?.current ?? 1);

  // md-up: comportamiento idéntico a la Table actual.
  const isMobile = screens.md === false;
  if (!isMobile) {
    return <Table {...props} />;
  }

  const {
    title,
    subtitle,
    actionsKey = 'actions',
    hiddenKeys = [],
  } = mobile;

  const colKey = (col, i) => col.key ?? col.dataIndex ?? i;
  const actionsCol = columns.find((c, i) => colKey(c, i) === actionsKey);
  const titleColIndex = 0;

  const cellValue = (col, record, index) => {
    const raw = col.dataIndex != null ? record?.[col.dataIndex] : undefined;
    if (typeof col.render === 'function') {
      return col.render(raw, record, index);
    }
    return raw;
  };

  const isEmptyValue = (v) =>
    v === null || v === undefined || v === '' ||
    (typeof v === 'string' && v.trim() === '');

  const getRowKey = (record, index) => {
    if (typeof rowKey === 'function') return rowKey(record, index);
    if (typeof rowKey === 'string') return record?.[rowKey] ?? index;
    return record?.key ?? record?.id ?? index;
  };

  // Paginación: server-side (onChange) muestra las filas dadas; client-side segmenta.
  const serverSide = !!(pag && typeof pag.onChange === 'function');
  const pageSize = pag?.pageSize ?? 10;
  const current = serverSide ? (pag?.current ?? 1) : localPage;
  const total = pag?.total ?? rows.length;

  const pageRows = (() => {
    if (!pag || serverSide) return rows;
    const start = (current - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  })();

  const emptyText = locale?.emptyText;

  const cardTitle = (record, index) => {
    if (typeof title === 'function') return title(record, index);
    const first = columns[titleColIndex];
    return first ? cellValue(first, record, index) : null;
  };

  return (
    <Spin spinning={!!loading}>
      {rows.length === 0 ? (
        <Empty description={emptyText || undefined} style={{ padding: '24px 0' }} />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%', display: 'flex' }}>
          {pageRows.map((record, index) => {
            const key = getRowKey(record, index);
            const bodyCols = columns.filter((col, i) => {
              const k = colKey(col, i);
              return i !== titleColIndex && k !== actionsKey && !hiddenKeys.includes(k);
            });
            const actions = actionsCol ? cellValue(actionsCol, record, index) : null;

            return (
              <Card key={key} size="small" styles={{ body: { padding: 14 } }}>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, wordBreak: 'break-word' }}>
                    {cardTitle(record, index)}
                  </div>
                  {typeof subtitle === 'function' && (
                    <Text type="secondary" style={{ fontSize: 12 }}>{subtitle(record, index)}</Text>
                  )}
                </div>

                {bodyCols.map((col, i) => {
                  const value = cellValue(col, record, index);
                  if (isEmptyValue(value)) return null;
                  return (
                    <div
                      key={colKey(col, i)}
                      style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0', alignItems: 'baseline' }}
                    >
                      <Text type="secondary" style={{ fontSize: 12, flex: '0 0 auto' }}>{col.title}</Text>
                      <span style={{ textAlign: 'right', wordBreak: 'break-word', minWidth: 0 }}>{value}</span>
                    </div>
                  );
                })}

                {actions && (
                  <>
                    <Divider style={{ margin: '10px 0 8px' }} />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
                      {actions}
                    </div>
                  </>
                )}
              </Card>
            );
          })}

          {pag && total > pageSize && (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 4 }}>
              <Pagination
                simple
                current={current}
                pageSize={pageSize}
                total={total}
                onChange={(page, size) => {
                  if (serverSide) pag.onChange(page, size);
                  else setLocalPage(page);
                }}
              />
            </div>
          )}
        </Space>
      )}
    </Spin>
  );
};

export default ResponsiveTable;
